import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Typography } from "@mui/material";
import { type FC, useMemo } from "react";

import { RichTooltip } from "~/components/UI/RichTooltip";
import { type IMarket } from "~/types/event";
import { formatPercentageValue } from "~/utils/formatValue";
import { chanceColumn, isUntraded, readMarketValue } from "~/utils/marketValue";

import * as Styled from "./TableField.styles";

export const TOOLTIP_TEXT =
  "This market has no executed trades yet.\n" +
  "Although buy and sell orders may exist, they do not establish a market price. Prices and implied probability become available only after the first trade is executed. Until then, Yes Price, No Price, and % Chance are shown as N/A.";

interface IPercentageChangeProps {
  market: IMarket;
}

export const PercentageChangeField: FC<IPercentageChangeProps> = ({
  market,
}) => {
  const percText = useMemo(
    () => chanceColumn.format(readMarketValue(chanceColumn, market)),
    [market]
  );

  const percChange24 = useMemo(() => {
    if (market.chanceChange24h === null) {
      return null;
    }

    return Math.round(market.chanceChange24h * 100);
  }, [market.chanceChange24h]);

  const formattedPercChange24 = useMemo(
    () => (percChange24 === null ? "N/A" : formatPercentageValue(percChange24)),
    [percChange24]
  );

  const isPercChange24Chip = useMemo(
    () => percChange24 !== null && Math.abs(percChange24) >= 1,
    [percChange24]
  );

  const percChange24Color = useMemo((): "success" | "error" => {
    if (percChange24 === null || percChange24 <= 0) {
      return "error";
    }

    return "success";
  }, [percChange24]);

  const percChange24Icon = useMemo(() => {
    if (percChange24 === null) {
      return undefined;
    }

    return percChange24 > 0 ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  }, [percChange24]);

  if (isUntraded(market.volume)) {
    return (
      <Typography
        variant="caption"
        sx={{
          position: "relative",
          padding: "0 0.8rem",
        }}
      >
        <RichTooltip title={TOOLTIP_TEXT} actionText={""}>
          <Styled.Icon
            color="disabled"
            fontSize="small"
            sx={{ fontSize: "14px" }}
          />
        </RichTooltip>
        N/A
      </Typography>
    );
  }

  return (
    <Styled.ChangeCell>
      <Typography variant="caption">{percText}</Typography>
      {market.chanceChange24h !== null && isPercChange24Chip && (
        <Styled.ChangeChip
          color={percChange24Color}
          label={formattedPercChange24}
          icon={percChange24Icon}
          size="small"
          data-skip-custom-styles="true"
          sx={{
            padding: "2px",
            height: "auto",
            margin: 0,
          }}
        />
      )}
    </Styled.ChangeCell>
  );
};
